import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import {
  ArrowLeftIcon,
  PencilIcon,
  TrashIcon,
  CloudIcon,
  ClockIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import {
  CloudProviderType,
  EnvironmentStatus,
  AzureDetails,
  GCPDetails,
  AWSDetails
} from '../../types/environment';

interface Environment {
  id: string;
  name: string;
  cloudProviderType: CloudProviderType;
  customerType: string;
  environmentType: string;
  status: EnvironmentStatus;
  companyId: string;
  
  azureDetails?: AzureDetails | boolean;
  gcpDetails?: GCPDetails | boolean;
  awsDetails?: AWSDetails | boolean;
  
  provisioningRequestId?: string;
  provisionedAt?: string;
  offboardingAt?: string;
  terminatedAt?: string;
  
  createdAt: string;
  updatedAt: string;
  
  company?: {
    id: string;
    name: string;
  };
  provisioningRequest?: {
    id: string;
    requestNumber: string;
    status: string;
    approvalStatus: string;
  };
  createdBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  updatedBy?: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
  };
  resources?: Array<{
    id: string;
    resourceType: string;
    resourceId: string;
    resourceName: string;
    status: string;
    metadata?: any;
    createdAt: string;
  }>;
  activities?: Array<{
    id: string;
    activityType: string;
    description: string;
    metadata?: any;
    performedAt: string;
    performedBy?: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
    };
  }>;
}

const EnvironmentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { formatDateTime } = useDateFormatter();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canDeleteEntity } = useUserPermissions();
  const [showProviderDetails, setShowProviderDetails] = useState(false);

  const { data: environment, isLoading, isError } = useQuery({
    queryKey: ['environment', id],
    queryFn: async () => {
      const response = await api.get(`/environments/${id}`);
      return response.data as Environment;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const response = await api.delete(`/environments/${id}`);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Environment deleted successfully');
      navigate('/environments');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete environment');
    },
  });

  const provisionMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/environments/${id}/provision`);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success(`Provisioning request ${data.requestNumber} created successfully`);
      navigate(`/environment-requests/${data.requestId}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create provisioning request');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading environment details...</div>
      </div>
    );
  }

  if (isError || !environment) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading environment details</div>
      </div>
    );
  }

  const getStatusBadgeColor = (status: EnvironmentStatus) => {
    switch (status) {
      case 'Open':
        return 'bg-gray-100 text-gray-800';
      case 'Provisioning':
        return 'bg-blue-100 text-blue-800';
      case 'Provisioned':
        return 'bg-green-100 text-green-800';
      case 'Offboarding':
        return 'bg-yellow-100 text-yellow-800';
      case 'Terminated':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getProviderBadgeColor = (provider: CloudProviderType) => {
    switch (provider) {
      case 'Azure':
        return 'bg-blue-100 text-blue-800';
      case 'AWS':
        return 'bg-orange-100 text-orange-800';
      case 'GCP':
        return 'bg-green-100 text-green-800';
      case 'Rackspace':
        return 'bg-purple-100 text-purple-800';
      case 'OnPremise':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Users can delete if they have delete permission AND environment is in deletable state
  const canDelete = user && canDeleteEntity('environments') &&
    (environment.status === 'Open' || environment.status === 'Terminated');

  const canProvision = environment.status === 'Open' && !environment.provisioningRequestId;

  const hasProviderDetails = typeof environment.azureDetails === 'object' || 
                            typeof environment.gcpDetails === 'object' || 
                            typeof environment.awsDetails === 'object';

  const getStatusProgress = (status: EnvironmentStatus) => {
    const statuses = ['Open', 'Provisioning', 'Provisioned', 'Offboarding', 'Terminated'];
    const currentIndex = statuses.indexOf(status);
    return {
      statuses,
      currentIndex
    };
  };

  const { statuses, currentIndex } = getStatusProgress(environment.status);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      {/* Status Progress Bar */}
      <div className="mb-8">
        <div className="bg-gray-100 p-1 rounded-md">
          <div className="flex">
            {statuses.map((status, index) => {
              const isCompleted = index < currentIndex;
              const isCurrent = index === currentIndex;
              const isLast = index === statuses.length - 1;
              const isTerminated = status === 'Terminated';
              
              // Determine the color based on state
              let bgColor, textColor;
              if (isTerminated && isCurrent) {
                // Terminated state when current - special color
                bgColor = 'bg-green-600';
                textColor = 'text-white';
              } else if (isCompleted) {
                // Completed states - gray with checkmark
                bgColor = 'bg-gray-300';
                textColor = 'text-gray-700';
              } else if (isCurrent) {
                // Current state - blue/primary color
                bgColor = 'bg-blue-500';
                textColor = 'text-white';
              } else {
                // Future states - white/light
                bgColor = 'bg-white';
                textColor = 'text-gray-500';
              }
              
              return (
                <div
                  key={status}
                  className="relative flex-1"
                  style={{ zIndex: statuses.length - index }}
                >
                  <div
                    className={`
                      relative flex items-center justify-center h-12 text-sm font-medium transition-all
                      ${bgColor} ${textColor}
                    `}
                    style={{
                      clipPath: isLast
                        ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
                        : 'polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%)',
                      marginRight: isLast ? '0' : '-12px',
                      paddingLeft: index === 0 ? '16px' : '24px',
                      paddingRight: isLast ? '16px' : '24px'
                    }}
                  >
                    <span className="flex items-center">
                      {isCompleted && (
                        <svg
                          className="mr-2 h-4 w-4"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                      {status}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="mb-8">
        <Link
          to="/environments"
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="mr-1 h-4 w-4" />
          Back to Environments
        </Link>
        
        <div className="mt-4 md:flex md:items-center md:justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
              {environment.name}
            </h2>
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <CloudIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-gray-400" />
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getProviderBadgeColor(environment.cloudProviderType)}`}>
                {environment.cloudProviderType}
              </span>
              <span className="mx-2">•</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(environment.status)}`}>
                {environment.status}
              </span>
            </div>
          </div>
          <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
            {canProvision && (
              <button
                onClick={() => provisionMutation.mutate()}
                disabled={provisionMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
              >
                <RocketLaunchIcon className="-ml-1 mr-2 h-5 w-5" />
                Provision
              </button>
            )}
            <Link
              to={`/environments/${id}/edit`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <PencilIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" />
              Edit
            </Link>
            {canDelete && (
              <button
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this environment?')) {
                    deleteMutation.mutate();
                  }
                }}
                disabled={deleteMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                <TrashIcon className="-ml-1 mr-2 h-5 w-5" />
                Delete
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column - Environment Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Environment Information
              </h3>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-0">
              <dl className="sm:divide-y sm:divide-gray-200">
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Environment Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {environment.environmentType}
                  </dd>
                </div>
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Customer Type</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {environment.customerType === 'ExistingCustomer' ? 'Existing Customer' : 'New Onboarding'}
                  </dd>
                </div>
                {environment.company && (
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Company</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {environment.company.name}
                    </dd>
                  </div>
                )}
                <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                  <dt className="text-sm font-medium text-gray-500">Created</dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                    {formatDateTime(environment.createdAt)}
                    {environment.createdBy && (
                      <span className="text-gray-500">
                        {' '}by {environment.createdBy.firstName} {environment.createdBy.lastName}
                      </span>
                    )}
                  </dd>
                </div>
                {environment.provisionedAt && (
                  <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6">
                    <dt className="text-sm font-medium text-gray-500">Provisioned</dt>
                    <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                      {formatDateTime(environment.provisionedAt)}
                    </dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Cloud Provider Details */}
          {hasProviderDetails && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Cloud Provider Configuration
                </h3>
                <button
                  onClick={() => setShowProviderDetails(!showProviderDetails)}
                  className="text-sm text-primary-600 hover:text-primary-900"
                >
                  {showProviderDetails ? 'Hide' : 'Show'} Details
                </button>
              </div>
              {showProviderDetails && (
                <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                  {environment.cloudProviderType === 'Azure' && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Azure Configuration</h4>
                      {typeof environment.azureDetails === 'object' && environment.azureDetails && Object.keys(environment.azureDetails).length > 0 ? (
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                          <div>
                            <dt className="text-xs text-gray-500">Application ID</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.azureDetails as AzureDetails).applicationId ? '•••••••' : '-'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Subscription ID</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.azureDetails as AzureDetails).subscriptionId ? '•••••••' : '-'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Tenant ID</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.azureDetails as AzureDetails).tenantId ? '•••••••' : '-'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Client Secret</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.azureDetails as AzureDetails).clientSecret ? '•••••••' : '-'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Subscription Name</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.azureDetails as AzureDetails).subscriptionName || '-'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Tenant Name</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.azureDetails as AzureDetails).tenantName || '-'}
                            </dd>
                          </div>
                          <div className="col-span-2">
                            <dt className="text-xs text-gray-500">CSP Subscription</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.azureDetails as AzureDetails).cspSubscription ? 'Yes' : 'No'}
                            </dd>
                          </div>
                        </dl>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No Azure configuration details have been added yet.</p>
                      )}
                    </div>
                  )}
                  {environment.cloudProviderType === 'GCP' && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">GCP Configuration</h4>
                      {typeof environment.gcpDetails === 'object' && environment.gcpDetails && Object.keys(environment.gcpDetails).length > 0 ? (
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                          <div>
                            <dt className="text-xs text-gray-500">Project Name</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.gcpDetails as GCPDetails).projectName || '-'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Service Account</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.gcpDetails as GCPDetails).serviceAccount ? '•••••••' : '-'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Client ID</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.gcpDetails as GCPDetails).clientId ? '•••••••' : '-'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Client Secret</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.gcpDetails as GCPDetails).clientSecret ? '•••••••' : '-'}
                            </dd>
                          </div>
                          <div className="col-span-2">
                            <dt className="text-xs text-gray-500">JWT Provider</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.gcpDetails as GCPDetails).jwtProvider ? '•••••••' : '-'}
                            </dd>
                          </div>
                        </dl>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No GCP configuration details have been added yet.</p>
                      )}
                    </div>
                  )}
                  {environment.cloudProviderType === 'AWS' && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">AWS Configuration</h4>
                      {typeof environment.awsDetails === 'object' && environment.awsDetails && Object.keys(environment.awsDetails).length > 0 ? (
                        <dl className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
                          <div>
                            <dt className="text-xs text-gray-500">Access Key ID</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.awsDetails as AWSDetails).clientId ? '•••••••' : '-'}
                            </dd>
                          </div>
                          <div>
                            <dt className="text-xs text-gray-500">Secret Access Key</dt>
                            <dd className="mt-1 text-sm text-gray-900">
                              {(environment.awsDetails as AWSDetails).clientSecretKey ? '•••••••' : '-'}
                            </dd>
                          </div>
                        </dl>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No AWS configuration details have been added yet.</p>
                      )}
                    </div>
                  )}
                  {environment.cloudProviderType === 'Rackspace' && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Rackspace Configuration</h4>
                      <p className="text-sm text-gray-500 italic">
                        Rackspace configuration is managed through their portal.
                      </p>
                    </div>
                  )}
                  {environment.cloudProviderType === 'OnPremise' && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">On-Premise Configuration</h4>
                      <p className="text-sm text-gray-500 italic">
                        On-Premise environments don't require cloud credentials.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Resources */}
          {environment.resources && environment.resources.length > 0 && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Deployed Resources
                </h3>
              </div>
              <div className="border-t border-gray-200">
                <ul className="divide-y divide-gray-200">
                  {environment.resources.map((resource) => (
                    <li key={resource.id} className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {resource.resourceName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {resource.resourceType} • {resource.resourceId}
                          </p>
                        </div>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {resource.status}
                        </span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Request and Activities */}
        <div className="space-y-6">
          {/* Provisioning Request */}
          {environment.provisioningRequest && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Provisioning Request
                </h3>
              </div>
              <div className="border-t border-gray-200 px-4 py-5">
                <dl className="space-y-3">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Request Number</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      <Link
                        to={`/environment-requests/${environment.provisioningRequest.id}`}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        {environment.provisioningRequest.requestNumber}
                      </Link>
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {environment.provisioningRequest.status}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Approval Status</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {environment.provisioningRequest.approvalStatus}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {/* Activity Timeline */}
          {environment.activities && environment.activities.length > 0 && (
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Recent Activities
                </h3>
              </div>
              <div className="border-t border-gray-200 px-4 py-5">
                <div className="flow-root">
                  <ul className="-mb-8">
                    {environment.activities.slice(0, 5).map((activity, index) => (
                      <li key={activity.id}>
                        <div className="relative pb-8">
                          {index !== Math.min(4, environment.activities!.length - 1) && (
                            <span
                              className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                              aria-hidden="true"
                            />
                          )}
                          <div className="relative flex space-x-3">
                            <div>
                              <span className="h-8 w-8 rounded-full bg-gray-400 flex items-center justify-center ring-8 ring-white">
                                <ClockIcon className="h-4 w-4 text-white" />
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div>
                                <p className="text-sm text-gray-900">
                                  {activity.description}
                                </p>
                                {activity.performedBy && (
                                  <p className="text-xs text-gray-500">
                                    by {activity.performedBy.firstName} {activity.performedBy.lastName}
                                  </p>
                                )}
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                {formatDateTime(activity.performedAt)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                {environment.activities.length > 5 && (
                  <div className="mt-6">
                    <Link
                      to={`/environments/${id}/activities`}
                      className="text-sm text-primary-600 hover:text-primary-900"
                    >
                      View all activities →
                    </Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnvironmentDetail;